/**
 * Consulta de CEP no ViaCEP (https://viacep.com.br).
 *
 * Chamada direta do navegador: o serviço é público, sem chave, e libera CORS.
 * Passar pelo nosso backend só acrescentaria um salto — e uma indisponibilidade
 * a mais — para um dado que não é sigiloso.
 *
 * O ViaCEP responde 200 com `{ "erro": true }` para CEP inexistente, então não
 * dá para confiar só no status HTTP.
 */

export interface EnderecoCep {
  cep: string;
  logradouro: string;
  bairro: string;
  /** Nome do município (campo `localidade` do ViaCEP). */
  municipio: string;
  uf: string;
  complemento: string;
}

interface RespostaViaCep {
  cep?: string;
  logradouro?: string;
  bairro?: string;
  localidade?: string;
  uf?: string;
  complemento?: string;
  erro?: boolean | string;
}

/** Só os dígitos; CEP válido tem 8. */
export function apenasDigitosCep(cep: string): string {
  return cep.replace(/\D/g, '');
}

export function cepCompleto(cep: string): boolean {
  return apenasDigitosCep(cep).length === 8;
}

/**
 * Busca o endereço do CEP. Devolve `null` quando o CEP não existe, e lança em
 * falha de rede — são situações diferentes para quem chama: uma é "confira o
 * número", a outra é "tente de novo".
 *
 * Timeout curto de propósito: o cliente está com o teclado aberto esperando os
 * campos preencherem; passar disso, é melhor deixá-lo digitar.
 */
export async function buscarCep(cep: string, timeoutMs = 8000): Promise<EnderecoCep | null> {
  const digitos = apenasDigitosCep(cep);
  if (digitos.length !== 8) return null;

  const controle = new AbortController();
  const timer = setTimeout(() => controle.abort(), timeoutMs);
  try {
    const resposta = await fetch(`https://viacep.com.br/ws/${digitos}/json/`, {
      signal: controle.signal,
    });
    if (!resposta.ok) throw new Error(`ViaCEP respondeu ${resposta.status}`);

    const dados = (await resposta.json()) as RespostaViaCep;
    if (dados.erro) return null;

    return {
      cep: dados.cep ?? digitos,
      logradouro: dados.logradouro ?? '',
      bairro: dados.bairro ?? '',
      municipio: dados.localidade ?? '',
      uf: dados.uf ?? '',
      complemento: dados.complemento ?? '',
    };
  } finally {
    clearTimeout(timer);
  }
}
